/**
 * 会话消息的前端内存缓存。
 * 用于在页面切换或返回会话时快速恢复消息，减少重复请求和界面闪烁。
 */
import { useState } from "#app";

type MessageCache = Record<string, any[]>;

const cloneMessages = (messages: any[]) =>
  messages.map((message) => ({ ...message }));

const belongsToSession = (message: any, sessionId: string) => {
  // 兼容 SDK 临时消息没有 sessionId 的情况，但拒绝明显属于其他会话的数据。
  return !message?.sessionId || message.sessionId === sessionId;
};

export function useSessionMessageStore() {
  const cache = useState<MessageCache>("chat-session-messages", () => ({})); // 初始化缓存为空对象

  /**
   * 获取会话消息
   * @param sessionId
   * @returns
   */
  const getMessages = (sessionId?: string) => {
    if (!sessionId) {
      return [];
    }

    return cloneMessages(cache.value[sessionId] || []);
  };

  /**
   * 设置会话消息
   * @param sessionId
   * @param messages
   * @returns
   */
  const setMessages = (sessionId: string | undefined, messages: any[]) => {
    if (!sessionId || messages.length === 0) {
      return;
    }

    if (!messages.every((message) => belongsToSession(message, sessionId))) {
      // 会话快速切换时可能收到迟到的流式更新，拒写可避免污染当前会话缓存。
      return;
    }

    cache.value = {
      ...cache.value,
      [sessionId]: cloneMessages(messages),
    };
  };

  /**
   * 清除会话消息
   * @param sessionId
   * @returns
   */
  const clearMessages = (sessionId?: string) => {
    if (!sessionId || !cache.value[sessionId]) {
      return;
    }

    const nextCache = { ...cache.value }; // 避免直接修改原始缓存
    delete nextCache[sessionId]; // 删除指定会话的消息
    cache.value = nextCache;
  };

  return {
    getMessages,
    setMessages,
    clearMessages,
  };
}
